import React, { useState } from 'react';
import { User, ChevronUp, ChevronDown } from 'lucide-react';
import { Message as MessageType } from '../utils/types';
import TableViewer from './TableViewer';
import AppIcon from "../utils/AppIcon";

interface MessageProps {
    message: MessageType;
}

interface ShowMoreProps {
    details: boolean;
    sql: boolean;
}

const Message: React.FC<MessageProps> = ({ message }) => {
    const [showMore, setShowMore] = useState<ShowMoreProps>({
        details: false,
        sql: false
    });

    const isUserMessage = message.type === 'question';

    const beforeBriefSummary = `${message?.content?.split('Brief Summary:')[0]}`;
    const briefSummary = `${message?.content?.split('Brief Summary:')[1]?.split('Detailed Explanation:')[0]}`;
    const detailedExplanation = `${message?.content?.split('Detailed Explanation:')[1]?.split('SQL Query:')[0]}<br>`;
    const sqlQuery = `<pre>${message?.content?.split('SQL Query:')[1]}</pre>`;

    const handleMore = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const target = (e.target as HTMLButtonElement)?.id as keyof ShowMoreProps;
        setShowMore((prev) => ({
            ...prev,
            [target]: !prev[target]
        }));
    }

    return (
        <div className={`flex p-4 text-left gap-4 ${isUserMessage ? 'bg-blue-50 border-b border-gray-200 ' : ' text-sm text-left'}`}>
            <div className="flex-shrink-0 mr-3">
                <div className={`w-8 h-8 rounded-full ${isUserMessage ? 'bg-blue-100 text-blue-600' : ' text-green-600'} flex items-center justify-center`}>
                    {isUserMessage ? <User size={16} /> : <AppIcon name="alex_ai_bot" size={32} />}
                </div>
            </div>
            <div className="flex-1 max-w-[calc(100%-40px)]">
                <div className="rounded-lg">
                    {message.type === 'answer' && message.content !== "Alex couldn't find the answer to your question." ? (
                        <>
                            {!!beforeBriefSummary &&
                                <p className="text-gray-800" dangerouslySetInnerHTML={{ __html: beforeBriefSummary }} />
                            }

                            {!!briefSummary &&
                                <p className="text-gray-800" dangerouslySetInnerHTML={{ __html: briefSummary }} />
                            }

                            <button
                                id="details"
                                className="text-blue-600 text-sm mt-3 mb-3 flex items-center"
                                onClick={(e) => handleMore(e)}
                            >
                                {showMore?.details
                                    ? <ChevronUp size={14} className="mr-1" />
                                    : <ChevronDown size={14} className="mr-1" />
                                }
                                Show details
                            </button>

                            {!!detailedExplanation && !!showMore?.details &&
                                <p className="text-gray-800" dangerouslySetInnerHTML={{ __html: detailedExplanation }} />
                            }

                            <button
                                id="sql"
                                className="text-blue-600 text-sm mt-3 mb-3 flex items-center"
                                onClick={(e) => handleMore(e)}
                            >
                                {showMore?.sql
                                    ? <ChevronUp size={14} className="mr-1" />
                                    : <ChevronDown size={14} className="mr-1" />
                                }
                                Show SQL query
                            </button>

                            {!!sqlQuery && !!showMore?.sql &&
                                <p className="text-gray-800" dangerouslySetInnerHTML={{ __html: sqlQuery }} />
                            }
                        </>
                    ) : (
                        <p className="text-gray-800">{message.content}</p>
                    )}

                    {message.tableData && (
                        <TableViewer data={message.tableData} />
                    )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                    {message.timestamp}
                </div>
            </div>
        </div>
    );
};

export default Message;